import React, { useState } from 'react';
import { useGate } from 'effector-react';
import { Scrollbars } from 'react-custom-scrollbars';
import { SortableElement, type SortEnd } from 'react-sortable-hoc';
import styled from 'styled-components';
import classNames from 'classnames';
import type { SceneData, HotspotGroupItem } from '@types';
import Title from '@components/Title';
import Tip from '@components/Tip';
import { TipWrapper } from '@components/TipWrapper';
import IconExit from '@components/icons/IconExit';
import Button from '@components/Button';
import { $GateHotspotGroupMode } from '@stores/hotspotStores';
import SortableList from '@components/SortableList';
import { $sceneMapApi } from '@stores/dataStores';
import arrayMove from '@utils/arrayMove';
import IconTrash from '@components/icons/IconTrash';
import SvgIcon from '@components/SvgIcon';
import useToggle from '@hooks/useToggle';
import SidebarHeader from '../../sidebar/components/SidebarHeader';
import { PanelGroupStyle } from './PanelGroupStyle';
import { PanelGroupSetting } from './PanelGroupSetting';

interface Props {
  scene: SceneData;
  onQuitClick: () => void;
}

const MAX = 50;

export function HotspotGroupModeList({ scene, onQuitClick }: Props) {
  const hotspotList = scene.hotspot ?? [];
  const groupSetting = scene.groups ?? {};
  const groupList = groupSetting.list ?? [];
  const loading = useToggle(false);
  const stylePanel = useToggle(false);
  const [editingIndex, setEditingIndex] = useState<null | number>(null);
  useGate($GateHotspotGroupMode, true);

  const addGroup = () => {
    loading.on();
    setEditingIndex(groupList.length);
    loading.off();
  };

  const handleSortEnd = (sort: SortEnd) => {
    $sceneMapApi.updateScene({
      id: scene.id,
      updater: (draft) => {
        const list = draft.groups?.list;
        if (!list) return;
        draft.groups!.list = arrayMove({
          array: list,
          fromIndex: sort.oldIndex,
          toIndex: sort.newIndex,
        });
      },
    });
  };

  const handleSetDefault = (id: string) => {
    $sceneMapApi.updateScene({
      id: scene.id,
      updater: (draft) => {
        draft.groups ??= {};
        if (draft.groups.defaultId === id) {
          draft.groups.defaultId = '';
        } else {
          draft.groups.defaultId = id;
        }
      },
    });
  };

  const handleDelete = (index: number) => {
    $sceneMapApi.updateScene({
      id: scene.id,
      updater: (draft) => {
        const list = draft.groups?.list;
        if (!list) return;
        list.splice(index, 1);
      },
    });
  };

  return (
    <>
      <SidebarHeader
        title={
          <div className="flex">
            <Title>热点</Title>
            <Tip className="ml-10 self-end text-base">{`分组设置 ${groupList.length}/${MAX}`}</Tip>
          </div>
        }
        btns={
          <TipWrapper text="退出操作" placement="bottom" size="sm">
            <IconExit className="focus:outline-none" onClick={onQuitClick} />
          </TipWrapper>
        }
      />
      <Button
        size="lg"
        className="mb-10 w-full"
        disabled={loading.isOn || groupList.length >= MAX}
        onClick={addGroup}
      >
        添加分组
      </Button>
      {groupList.length > 0 ? (
        <div className="relative flex-1">
          <SortableList
            axis="y"
            lockAxis="y"
            distance={10}
            onSortEnd={handleSortEnd}
          >
            <Scrollbars>
              {groupList.map((x, i) => (
                <Item
                  index={i}
                  listIndex={i}
                  key={x.id}
                  data={x}
                  hotspotCount={hotspotList.length}
                  isDefault={groupSetting.defaultId === x.id}
                  onClick={setEditingIndex}
                  onSetDefaultClick={handleSetDefault}
                  onDeleteClick={handleDelete}
                />
              ))}
            </Scrollbars>
          </SortableList>
        </div>
      ) : (
        <>
          <Tip className="mt-5 text-center text-base">
            {`每个场景最多可添加${MAX}个分组`}
          </Tip>
          <Tip className="mt-5 text-center text-base">
            添加后支持上下拖拽调整分组顺序
          </Tip>
        </>
      )}
      <Button className="mt-auto w-full" onClick={stylePanel.on}>
        分组高级设置
      </Button>
      <Button scheme="darkest" className="mt-10 w-full" onClick={onQuitClick}>
        退出分组
      </Button>
      {stylePanel.isOn && (
        <PanelGroupStyle
          sceneId={scene.id}
          data={groupSetting}
          onRequestClose={stylePanel.off}
        />
      )}
      {editingIndex !== null && (
        <PanelGroupSetting
          scene={scene}
          index={editingIndex}
          onRequestClose={() => setEditingIndex(null)}
        />
      )}
    </>
  );
}

const IconSet = styled(SvgIcon).attrs({
  children: ({ color }: { color: string }) => (
    <path
      d="M6.707 7.793 12.5 2 14 3.5l-7.293 7.121L2 5.914 3.414 4.5l3.293 3.293ZM2 12v2h12v-2H2Z"
      fillRule="evenodd"
      fill={color}
    />
  ),
})``;

type ItemProps = {
  data: HotspotGroupItem;
  hotspotCount: number;
  isDefault: boolean;
  listIndex: number;
  onClick: (index: number) => void;
  onSetDefaultClick: (id: string) => void;
  onDeleteClick: (index: number) => void;
};

const Item = SortableElement(
  ({
    data,
    hotspotCount,
    isDefault,
    listIndex,
    onClick,
    onSetDefaultClick,
    onDeleteClick,
  }: ItemProps) => {
    const count =
      data.hotspots.type === 1 ? hotspotCount : data.hotspots.list.length;
    return (
      <div
        className={classNames(
          'mb-10 flex h-35 cursor-pointer items-center rounded bg-black-#34 px-10 text-base text-white',
          {
            'ring-1 ring-inset ring-orange': isDefault,
          }
        )}
        onClick={() => onClick(listIndex)}
      >
        <div className="mr-10 truncate">{data.name}</div>
        <div className="ml-auto shrink-0 text-black-gray">{`${count}热点`}</div>
        <TipWrapper
          text="设为默认打开"
          placement="bottom"
          className="ml-10"
          size="sm"
        >
          <IconSet
            className="shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              onSetDefaultClick(data.id);
            }}
          />
        </TipWrapper>
        <TipWrapper text="删除" placement="bottom" className="ml-10" size="sm">
          <IconTrash
            className="shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              onDeleteClick(listIndex);
            }}
          />
        </TipWrapper>
      </div>
    );
  }
);
